import type { MetaFunction } from "@remix-run/cloudflare";
import { Link } from "@remix-run/react";
import { 
  Calendar,
  Monitor,
  Users,
  Star,
  Download,
  ShoppingCart,
  ExternalLink,
  GamepadIcon,
  Sword,
  Crown,
  Zap
} from "lucide-react";

export const meta: MetaFunction = () => {
  return [
    { title: "Stellar Blade PC - Ultimate Action Adventure Game" },
    { 
      name: "description", 
      content: "Experience the ultimate action adventure with Stellar Blade PC. Stunning visuals, intense combat, and full mod support. Available now with exclusive discounts." 
    },
    { name: "keywords", content: "stellar blade,pc game,action adventure,combat,rpg,mod support" },
    { name: "robots", content: "index,follow" },
    { rel: "canonical", href: "https://stellarblademod.com/Stellar-Blade-PC" },
    
    // Open Graph
    { property: "og:title", content: "Stellar Blade PC - Ultimate Action Adventure Game" },
    { property: "og:description", content: "Experience the ultimate action adventure with Stellar Blade PC. Stunning visuals, intense combat, and full mod support. Available now with exclusive discounts." },
    { property: "og:url", content: "https://stellarblademod.com/Stellar-Blade-PC" },
    { property: "og:type", content: "product" },
    { property: "og:image", content: "https://stellarblademod.com/mods/stellar-blade-nier-automata.webp" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: "Stellar Blade PC - Ultimate Action Adventure Game" },
    { name: "twitter:description", content: "Experience the ultimate action adventure with Stellar Blade PC. Stunning visuals, intense combat, and full mod support." },
    { name: "twitter:image", content: "https://stellarblademod.com/mods/stellar-blade-nier-automata.webp" },
  ];
};

export default function StellarBladePC() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Hero Section */}
      <div 
        className="relative h-96 bg-cover bg-center bg-no-repeat flex items-center"
        style={{
          backgroundImage: 'url(public/Stellar_Blade_pc.jpg)'
        }}
      >
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="container relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-4">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-500 bg-clip-text text-transparent">
                Stellar Blade PC
              </span>
            </h1>
            <p className="text-xl text-gray-300 mb-6 max-w-2xl">
              Experience breathtaking visuals, intense combat, and an unforgettable journey in this ultimate action-adventure masterpiece.
            </p>
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-300">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Release Date: 2025-06-11</span>
              </div>
              <div className="flex items-center gap-2">
                <Monitor className="w-4 h-4" />
                <span>Platform: PC (Steam)</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>Developer: Shift Up</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Left Column - Game Info */}
          <div className="lg:col-span-2 space-y-12">
            {/* Official Trailer & About Section */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
              <div className="space-y-8">
                {/* Official Trailer */}
                <div>
                  <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                    <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                    Official Trailer
                  </h2>
                  <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                    <iframe
                      className="absolute top-0 left-0 w-full h-full rounded-lg"
                      src="https://www.youtube.com/embed/Pex7jW3Tqwo"
                      title="Stellar Blade - Official Trailer"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  </div>
                </div>

                {/* About Section */}
                <div>
                  <h2 className="text-3xl font-bold text-white mb-6">About Stellar Blade</h2>
                  <p className="text-gray-300 text-lg leading-relaxed mb-6">
                    Stellar Blade™ is an action-adventure game set in a post-apocalyptic world where humanity's survival 
                    hangs in the balance. Players take control of EVE, a member of the 7th Airborne Squad, as she 
                    embarks on a mission to reclaim Earth from mysterious invaders.
                  </p>
                  <p className="text-gray-300 text-lg leading-relaxed mb-6">
                    Experience fast-paced melee-style combat with an arsenal of weapons including blades, machine guns, 
                    and laser blasters. Navigate through a beautifully crafted world that blends stunning visuals with 
                    intense action sequences that will keep you on the edge of your seat.
                  </p>
                  <p className="text-gray-300 text-lg leading-relaxed">
                    Developed with cutting-edge technology, Stellar Blade delivers a cinematic experience that combines 
                    fluid combat mechanics, exploration, and a compelling narrative in a world where every decision 
                    could determine humanity's fate.
                  </p>
                </div>
              </div>
            </div>

            {/* Key Features */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
                <Star className="w-8 h-8 text-yellow-400" />
                Key Features
              </h2>
              
                             <div className="grid md:grid-cols-2 gap-8">
                 <div className="space-y-6">
                   <div className="flex items-start gap-4">
                     <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
                       <Sword className="w-6 h-6 text-white" />
                     </div>
                     <div>
                       <h3 className="text-xl font-bold text-white mb-2">Fast-Paced Melee Combat</h3>
                       <p className="text-gray-400">Engage in dynamic melee-style combat with blades, machine guns, and laser blasters. Master fluid attack combinations and precise timing.</p>
                     </div>
                   </div>
                   
                   <div className="flex items-start gap-4">
                     <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center flex-shrink-0">
                       <Crown className="w-6 h-6 text-white" />
                     </div>
                     <div>
                       <h3 className="text-xl font-bold text-white mb-2">Post-Apocalyptic World</h3>
                       <p className="text-gray-400">Explore a beautifully crafted post-apocalyptic Earth filled with mysterious invaders and stunning environments.</p>
                     </div>
                   </div>
                   
                   <div className="flex items-start gap-4">
                     <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                       <Star className="w-6 h-6 text-white" />
                     </div>
                     <div>
                       <h3 className="text-xl font-bold text-white mb-2">Cinematic Experience</h3>
                       <p className="text-gray-400">Immerse yourself in a movie-like experience with stunning visuals and dramatic combat effects.</p>
                     </div>
                   </div>
                 </div>
                 
                 <div className="space-y-6">
                   <div className="flex items-start gap-4">
                     <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                       <GamepadIcon className="w-6 h-6 text-white" />
                     </div>
                     <div>
                       <h3 className="text-xl font-bold text-white mb-2">Full PC Optimization</h3>
                       <p className="text-gray-400">Experience 4K Ultra HD visuals, 120fps support, and comprehensive graphics options tailored for PC gaming.</p>
                     </div>
                   </div>
                   
                   <div className="flex items-start gap-4">
                     <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
                       <Zap className="w-6 h-6 text-white" />
                     </div>
                     <div>
                       <h3 className="text-xl font-bold text-white mb-2">Mod Support Ready</h3>
                       <p className="text-gray-400">Built with modding in mind - customize your experience with community-created content and enhancements.</p>
                     </div>
                   </div>
                   
                   <div className="flex items-start gap-4">
                     <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                       <Users className="w-6 h-6 text-white" />
                     </div>
                     <div>
                       <h3 className="text-xl font-bold text-white mb-2">Character Customization</h3>
                       <p className="text-gray-400">Play as EVE with various costume options and character progression throughout your journey.</p>
                     </div>
                   </div>
                 </div>
               </div>
            </div>

            {/* System Requirements */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
              <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
                <Monitor className="w-8 h-8 text-cyan-400" />
                System Requirements
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Minimum Requirements</h3>
                  <div className="space-y-3 text-gray-300">
                    <div><span className="text-cyan-400 font-semibold">OS:</span> Windows 10 64-bit</div>
                    <div><span className="text-cyan-400 font-semibold">Processor:</span> Intel® Core™ i5-7600k / AMD Ryzen™ 5 1600X</div>
                    <div><span className="text-cyan-400 font-semibold">Memory:</span> 16 GB RAM</div>
                    <div><span className="text-cyan-400 font-semibold">Graphics:</span> NVIDIA GeForce GTX 1060 6GB / AMD Radeon RX 580 8GB</div>
                    <div><span className="text-cyan-400 font-semibold">DirectX:</span> Version 12</div>
                    <div><span className="text-cyan-400 font-semibold">Storage:</span> 75 GB available space</div>
                  </div>
                  <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                    <p className="text-yellow-400 text-sm">
                      <strong>Note:</strong> Requires 64-bit processor and operating system. 1080P, Graphics Quality Preset "Low", 60FPS Target. HDD Supported, SSD Recommended.
                    </p>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-xl font-bold text-white mb-4">Recommended Requirements</h3>
                  <div className="space-y-3 text-gray-300">
                    <div><span className="text-purple-400 font-semibold">OS:</span> Windows 10 64-bit</div>
                    <div><span className="text-purple-400 font-semibold">Processor:</span> Intel® Core™ i5-8400 / AMD Ryzen™ 5 3600X</div>
                    <div><span className="text-purple-400 font-semibold">Memory:</span> 16 GB RAM</div>
                    <div><span className="text-purple-400 font-semibold">Graphics:</span> NVIDIA GeForce RTX 2060 SUPER / AMD Radeon RX 5700 XT</div>
                    <div><span className="text-purple-400 font-semibold">DirectX:</span> Version 12</div>
                    <div><span className="text-purple-400 font-semibold">Storage:</span> 75 GB available space</div>
                  </div>
                  <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <p className="text-green-400 text-sm">
                      <strong>Note:</strong> Requires 64-bit processor and operating system. 1440p, Graphics Quality Preset "Medium", 60FPS Target. SSD Required.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Purchase Options */}
          <div className="space-y-8">
            {/* Main Purchase Card */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 sticky top-8">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-400 font-semibold">AVAILABLE NOW</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Stellar Blade PC</h3>
                <p className="text-gray-400">Digital Download • Instant Access</p>
              </div>
              
                             <div className="space-y-4 mb-6">
                 <a
                   href="https://store.steampowered.com/app/3489700/_/"
                   target="_blank"
                   rel="noopener noreferrer"
                   className="flex items-center justify-center gap-3 w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300"
                 >
                   <Download className="w-5 h-5" />
                   Buy on Steam
                   <ExternalLink className="w-4 h-4" />
                 </a>
                 
                 <a
                   href="https://store.epicgames.com/p/stellar-blade-fa21c3"
                   target="_blank"
                   rel="noopener noreferrer"
                   className="flex items-center justify-center gap-3 w-full bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-800 hover:to-gray-900 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300"
                 >
                   <ShoppingCart className="w-5 h-5" />
                   Epic Games Store
                   <ExternalLink className="w-4 h-4" />
                 </a>
               </div>
              
                             <div className="border-t border-white/20 pt-4">
                 <h4 className="text-lg font-bold text-white mb-4">Best Deals</h4>
                 <div className="max-h-80 overflow-y-auto space-y-3 pr-2">
                   {/* Gamebillet Deal - Best */}
                   <div className="bg-green-600/20 border border-green-500/30 rounded-lg p-3">
                     <div className="flex items-center justify-between mb-2">
                       <div className="flex items-center gap-2">
                         <div className="w-6 h-6 bg-orange-500 rounded-sm flex items-center justify-center">
                           <span className="text-white text-xs font-bold">G</span>
                         </div>
                         <span className="text-white text-sm font-medium">Gamebillet</span>
                         <span className="bg-green-500 text-white text-xs px-2 py-1 rounded">Best deal</span>
                       </div>
                       <div className="text-right">
                         <div className="text-gray-400 text-xs line-through">$59.99</div>
                         <div className="text-white font-bold">$53.95</div>
                         <div className="text-green-400 text-xs">-10%</div>
                       </div>
                     </div>
                     <a 
                       href="https://www.gamebillet.com/stellar-blade-pre-order" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="block w-full bg-green-600 hover:bg-green-700 text-white text-center py-2 rounded text-sm font-medium transition-colors"
                     >
                       Shop Now ↗
                     </a>
                   </div>

                   {/* Gamersgate Deal */}
                   <div className="bg-white/10 border border-white/20 rounded-lg p-3">
                     <div className="flex items-center justify-between mb-2">
                       <div className="flex items-center gap-2">
                         <div className="w-6 h-6 bg-blue-600 rounded-sm flex items-center justify-center">
                           <span className="text-white text-xs font-bold">G</span>
                         </div>
                         <span className="text-white text-sm font-medium">Gamersgate</span>
                         <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded">-10% with GGD</span>
                       </div>
                       <div className="text-right">
                         <div className="text-gray-400 text-xs line-through">$59.99</div>
                         <div className="text-white font-bold">$53.99</div>
                         <div className="text-green-400 text-xs">-10%</div>
                       </div>
                     </div>
                     <a 
                       href="https://www.gamersgate.com/es/product/stellar-bladetm/" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center py-2 rounded text-sm font-medium transition-colors"
                     >
                       Shop Now ↗
                     </a>
                   </div>

                   {/* Gamesplanet Deal */}
                   <div className="bg-white/10 border border-white/20 rounded-lg p-3">
                     <div className="flex items-center justify-between mb-2">
                       <div className="flex items-center gap-2">
                         <div className="w-6 h-6 bg-purple-600 rounded-sm flex items-center justify-center">
                           <span className="text-white text-xs font-bold">G</span>
                         </div>
                         <span className="text-white text-sm font-medium">Gamesplanet</span>
                       </div>
                       <div className="text-right">
                         <div className="text-gray-400 text-xs line-through">$59.99</div>
                         <div className="text-white font-bold">$53.99</div>
                         <div className="text-green-400 text-xs">-10%</div>
                       </div>
                     </div>
                     <a 
                       href="https://gamesplanet.com/game/stellar-blade-steam-key--7505-1" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="block w-full bg-purple-600 hover:bg-purple-700 text-white text-center py-2 rounded text-sm font-medium transition-colors"
                     >
                       Shop Now ↗
                     </a>
                   </div>

                   {/* Fanatical Deal */}
                   <div className="bg-white/10 border border-white/20 rounded-lg p-3">
                     <div className="flex items-center justify-between mb-2">
                       <div className="flex items-center gap-2">
                         <div className="w-6 h-6 bg-orange-600 rounded-sm flex items-center justify-center">
                           <span className="text-white text-xs font-bold">F</span>
                         </div>
                         <span className="text-white text-sm font-medium">Fanatical</span>
                         <span className="bg-yellow-500 text-black text-xs px-2 py-1 rounded">1 month left</span>
                       </div>
                       <div className="text-right">
                         <div className="text-gray-400 text-xs line-through">$59.99</div>
                         <div className="text-white font-bold">$53.99</div>
                         <div className="text-green-400 text-xs">-10%</div>
                       </div>
                     </div>
                     <a 
                       href="https://www.fanatical.com/game/stellar-blade" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="block w-full bg-orange-600 hover:bg-orange-700 text-white text-center py-2 rounded text-sm font-medium transition-colors"
                     >
                       Shop Now ↗
                     </a>
                   </div>

                   {/* JoyBuggy Deal */}
                   <div className="bg-white/10 border border-white/20 rounded-lg p-3">
                     <div className="flex items-center justify-between mb-2">
                       <div className="flex items-center gap-2">
                         <div className="w-6 h-6 bg-teal-600 rounded-sm flex items-center justify-center">
                           <span className="text-white text-xs font-bold">J</span>
                         </div>
                         <span className="text-white text-sm font-medium">JoyBuggy</span>
                         <span className="bg-red-500 text-white text-xs px-2 py-1 rounded">⚠ 1</span>
                       </div>
                       <div className="text-right">
                         <div className="text-gray-400 text-xs line-through">$59.99</div>
                         <div className="text-white font-bold">$55.73</div>
                         <div className="text-green-400 text-xs">-7%</div>
                       </div>
                     </div>
                     <a 
                       href="https://www.joybuggy.com/stellar-blade" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="block w-full bg-teal-600 hover:bg-teal-700 text-white text-center py-2 rounded text-sm font-medium transition-colors"
                     >
                       Shop Now ↗
                     </a>
                   </div>

                   {/* Humble Bundle Deal */}
                   <div className="bg-white/10 border border-white/20 rounded-lg p-3">
                     <div className="flex items-center justify-between mb-2">
                       <div className="flex items-center gap-2">
                         <div className="w-6 h-6 bg-gray-800 rounded-sm flex items-center justify-center">
                           <span className="text-white text-xs font-bold">H</span>
                         </div>
                         <span className="text-white text-sm font-medium">Humble Bundle</span>
                       </div>
                       <div className="text-right">
                         <div className="text-white font-bold">$59.99</div>
                         <div className="text-gray-400 text-xs">No discount</div>
                       </div>
                     </div>
                     <a 
                       href="https://www.humblebundle.com/store/stellar-blade" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="block w-full bg-gray-700 hover:bg-gray-800 text-white text-center py-2 rounded text-sm font-medium transition-colors"
                     >
                       Shop Now ↗
                     </a>
                   </div>
                 </div>

                 {/* More Deals Link */}
                 <div className="pt-4">
                   <a 
                     href="https://gg.deals/game/stellar-blade-pc/" 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="flex items-center justify-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm transition-colors"
                   >
                     <ExternalLink className="w-4 h-4" />
                     View all deals on GG.deals
                   </a>
                 </div>
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}